import {
  CompanySettings,
  CompanySettingsValues,
  GstBacklog,
  GstChecks,
  GstZohoRegistration,
} from "../../domain/entities/CompanySettings";
import { SettingsRepository } from "../../domain/repositories/SettingsRepository";
import { SettingsApiDataSource } from "../datasources/SettingsApiDataSource";
import { CompanySettingsMapper } from "../mappers/CompanySettingsMapper";

/** SettingsRepository backed by the v1 settings API. */
export class SettingsRepositoryImpl implements SettingsRepository {
  constructor(private readonly dataSource: SettingsApiDataSource) {}

  async getCompanySettings(signal?: AbortSignal): Promise<CompanySettings> {
    const dto = await this.dataSource.fetchCompanySettings(signal);

    return CompanySettingsMapper.toDomain(dto);
  }

  async updateCompanySettings(
    changes: Partial<CompanySettingsValues>,
    signal?: AbortSignal
  ): Promise<CompanySettings> {
    const dto = await this.dataSource.saveCompanySettings(
      changes as Record<string, string>,
      signal
    );

    return CompanySettingsMapper.toDomain(dto);
  }

  async getGstSettings(signal?: AbortSignal): Promise<CompanySettings> {
    const dto = await this.dataSource.fetchGstSettings(signal);

    return CompanySettingsMapper.toDomain(dto);
  }

  async updateGstSettings(
    changes: Partial<CompanySettingsValues>,
    signal?: AbortSignal
  ): Promise<CompanySettings> {
    const dto = await this.dataSource.saveGstSettings(changes as Record<string, string>, signal);

    return CompanySettingsMapper.toDomain(dto);
  }

  async getGstZohoStatus(
    signal?: AbortSignal
  ): Promise<{ zoho: GstZohoRegistration; backlog: GstBacklog }> {
    const dto = await this.dataSource.fetchGstZohoStatus(signal);

    return {
      zoho: CompanySettingsMapper.toZoho(dto.zoho),
      backlog: CompanySettingsMapper.toBacklog(dto.backlog),
    };
  }

  async syncGstTaxes(signal?: AbortSignal): Promise<{ added: number; total: number }> {
    const dto = await this.dataSource.syncGstTaxes(signal);

    return {
      added: dto.new_added ?? 0,
      total: dto.total_from_zoho ?? 0,
    };
  }

  async getGstChecks(signal?: AbortSignal): Promise<GstChecks> {
    const dto = await this.dataSource.fetchGstChecks(signal);

    return CompanySettingsMapper.toChecks(dto);
  }

  async useZohoGstin(signal?: AbortSignal): Promise<CompanySettings> {
    const dto = await this.dataSource.useZohoGstin(signal);

    return CompanySettingsMapper.toDomain(dto);
  }
}
